import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import Chart from "chart.js/auto";

const ReportPreviewContent = styled.div`
  background: white;
  width: 21cm; /* A4 width */
  max-height: 87vh; /* Limit height to fit in viewport */
  margin: 0 auto;
  padding: 1.5cm 1.5cm 0.8cm 1.5cm; /* Reduced bottom padding to accommodate button */
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
  position: relative;
  color: #333;
  overflow-y: auto; /* Enable scrolling */
  display: flex;
  flex-direction: column; /* Ensures content flows top to bottom */
`;

const ReportHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    margin-bottom: 0.25rem;
  }
`;

const ChartSection = styled.div`
  margin-bottom: 1.2rem; /* Reduced margin */
`;

const ChartContainer = styled.div`
  height: 200px; /* Reduced height */
  width: 100%;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const DownloadButton = styled.button`
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky; /* Changed from fixed to sticky */
  bottom: 20px;
  margin: 2rem auto 0 auto; /* Added margin to position at the bottom of content */
  z-index: 10005;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  width: fit-content; /* Ensure button width fits content */

  &:hover {
    background: #1976d2;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    background: #90caf9;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: ${spinAnimation} 1s ease-in-out infinite;
`;

const ReportPreview = ({
  reportStats,
  reviewTimeStats,
  courseStats,
  companyRatingStats,
  internshipCountStats,
  onDownload,
  downloading = false,
}) => {
  const chartRefs = useRef({});

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  useEffect(() => {
    // Create Report Statistics Chart
    const reportCtx = document.getElementById("reportStatsPreview");
    if (reportCtx) {
      if (chartRefs.current.reportChart) {
        chartRefs.current.reportChart.destroy();
      }
      chartRefs.current.reportChart = new Chart(reportCtx, {
        type: "pie",
        data: {
          labels: ["Accepted", "Rejected", "Flagged"],
          datasets: [
            {
              data: [
                reportStats.accepted,
                reportStats.rejected,
                reportStats.flagged,
              ],
              backgroundColor: [
                "rgba(99, 202, 143, 0.8)",
                "rgba(239, 68, 68, 0.8)",
                "rgba(245, 158, 11, 0.8)",
              ],
              borderColor: [
                "rgba(99, 202, 143, 1)",
                "rgba(239, 68, 68, 1)",
                "rgba(245, 158, 11, 1)",
              ],
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                color: "#333",
              },
            },
          },
        },
      });
    }

    // Create Review Time Chart
    const reviewCtx = document.getElementById("reviewTimePreview");
    if (reviewCtx) {
      if (chartRefs.current.reviewChart) {
        chartRefs.current.reviewChart.destroy();
      }
      chartRefs.current.reviewChart = new Chart(reviewCtx, {
        type: "bar",
        data: {
          labels: Object.keys(reviewTimeStats),
          datasets: [
            {
              label: "Average Review Time (Days)",
              data: Object.values(reviewTimeStats),
              backgroundColor: "rgba(99, 102, 241, 0.6)",
              borderColor: "rgba(99, 102, 241, 1)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              labels: {
                color: "#333",
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    }

    // Create Top Courses Chart
    const coursesCtx = document.getElementById("topCoursesPreview");
    if (coursesCtx) {
      if (chartRefs.current.coursesChart) {
        chartRefs.current.coursesChart.destroy();
      }
      chartRefs.current.coursesChart = new Chart(coursesCtx, {
        type: "doughnut",
        data: {
          labels: Object.keys(courseStats),
          datasets: [
            {
              data: Object.values(courseStats),
              backgroundColor: [
                "rgba(99, 102, 241, 0.8)",
                "rgba(124, 58, 237, 0.8)",
                "rgba(217, 70, 239, 0.8)",
                "rgba(236, 72, 153, 0.8)",
                "rgba(244, 114, 182, 0.8)",
              ],
              borderColor: [
                "rgba(99, 102, 241, 1)",
                "rgba(124, 58, 237, 1)",
                "rgba(217, 70, 239, 1)",
                "rgba(236, 72, 153, 1)",
                "rgba(244, 114, 182, 1)",
              ],
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "right",
              labels: {
                color: "#333",
              },
            },
          },
        },
      });
    }

    // Create Top Rated Companies Chart
    const ratingCtx = document.getElementById("companiesRatingPreview");
    if (ratingCtx) {
      if (chartRefs.current.ratingChart) {
        chartRefs.current.ratingChart.destroy();
      }
      chartRefs.current.ratingChart = new Chart(ratingCtx, {
        type: "bar",
        data: {
          labels: Object.keys(companyRatingStats),
          datasets: [
            {
              label: "Average Rating",
              data: Object.values(companyRatingStats),
              backgroundColor: "rgba(56, 189, 248, 0.6)",
              borderColor: "rgba(56, 189, 248, 1)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: "y",
          plugins: {
            legend: {
              labels: {
                color: "#333",
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              max: 5,
            },
          },
        },
      });
    }

    // Create Top Companies by Internship Count Chart
    const countCtx = document.getElementById("companiesCountPreview");
    if (countCtx) {
      if (chartRefs.current.countChart) {
        chartRefs.current.countChart.destroy();
      }
      chartRefs.current.countChart = new Chart(countCtx, {
        type: "bar",
        data: {
          labels: Object.keys(internshipCountStats),
          datasets: [
            {
              label: "Internship Count",
              data: Object.values(internshipCountStats),
              backgroundColor: "rgba(34, 211, 238, 0.6)",
              borderColor: "rgba(34, 211, 238, 1)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: "y",
          plugins: {
            legend: {
              labels: {
                color: "#333",
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
            },
          },
        },
      });
    }

    return () => {
      // Cleanup charts on unmount
      Object.values(chartRefs.current).forEach((chart) => {
        if (chart && typeof chart.destroy === "function") {
          try {
            chart.destroy();
          } catch (error) {
            console.error("Error destroying chart:", error);
          }
        }
      });
    };
  }, [
    reportStats,
    reviewTimeStats,
    courseStats,
    companyRatingStats,
    internshipCountStats,
  ]);

  return (
    <ReportPreviewContent>
      <ReportHeader>
        <h1>SCAD Office Statistical Report</h1>
        <p>Generated on: {formatDate(new Date())}</p>
        <p>Student Career Advising Department</p>
      </ReportHeader>
      <ChartSection>
        <SectionTitle>Report Statistics</SectionTitle>
        <ChartContainer>
          <canvas id="reportStatsPreview"></canvas>
        </ChartContainer>
        <p>
          Total reports:{" "}
          {reportStats.accepted + reportStats.rejected + reportStats.flagged}
        </p>
      </ChartSection>
      <ChartSection>
        <SectionTitle>Average Review Time (Days)</SectionTitle>
        <ChartContainer>
          <canvas id="reviewTimePreview"></canvas>
        </ChartContainer>
      </ChartSection>
      <ChartSection>
        <SectionTitle>Most Frequently Used Courses</SectionTitle>
        <ChartContainer>
          <canvas id="topCoursesPreview"></canvas>
        </ChartContainer>
      </ChartSection>
      <ChartSection>
        <SectionTitle>Top Rated Companies</SectionTitle>
        <ChartContainer>
          <canvas id="companiesRatingPreview"></canvas>
        </ChartContainer>
      </ChartSection>{" "}
      <ChartSection>
        <SectionTitle>Top Companies by Internship Count</SectionTitle>
        <ChartContainer>
          <canvas id="companiesCountPreview"></canvas>
        </ChartContainer>
      </ChartSection>
      <DownloadButton onClick={onDownload} disabled={downloading}>
        {downloading && <LoadingSpinner />}
        {downloading ? "Generating PDF..." : "Download Report as PDF"}
      </DownloadButton>
    </ReportPreviewContent>
  );
};

export default ReportPreview;
